import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Warehouse, Truck, Boxes, AlertTriangle } from "lucide-react";
import api from "@/api";
import TopBar from "@/components/TopBar";
import { useLang } from "@/context/LanguageContext";

// Phase G (Feb 2026): Full-screen Depot + On-Road Stock summary.
export default function DepotStockPage() {
  const nav = useNavigate();
  const { t, lang } = useLang();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/depot/stock/summary").then(r => setD(r.data)).finally(() => setLoading(false));
  }, []);

  if (loading || !d) return <div className="min-h-screen grid place-items-center text-[#86868B]">{t("loading")}</div>;

  return (
    <div className="min-h-screen bg-[#F5F5F7] pb-10 page-slide-in">
      <TopBar title={t("depot_stock")} onBack={() => nav("/management")} />
      <div className="max-w-5xl mx-auto p-4 sm:p-6 space-y-5 fade-up">
        <div className="grid grid-cols-2 gap-3" data-testid="dp-summary">
          <div className="bg-gradient-to-br from-cyan-600 to-cyan-800 text-white rounded-3xl p-5 shadow-xl">
            <Warehouse className="w-7 h-7 mb-2" />
            <div className="text-[10px] uppercase tracking-wider opacity-80 font-bold">{t("depot_stock")}</div>
            <div className="heading-font text-4xl font-bold mt-1">{d.depot_total}</div>
            <div className="text-xs opacity-80">{t("boxes")}</div>
          </div>
          <div className="bg-gradient-to-br from-emerald-600 to-emerald-800 text-white rounded-3xl p-5 shadow-xl">
            <Truck className="w-7 h-7 mb-2" />
            <div className="text-[10px] uppercase tracking-wider opacity-80 font-bold">{t("on_road_stock")}</div>
            <div className="heading-font text-4xl font-bold mt-1">{d.on_road_total}</div>
            <div className="text-xs opacity-80">{t("boxes")}</div>
          </div>
        </div>

        <div className="bg-white rounded-2xl soft-shadow overflow-hidden" data-testid="dp-products">
          <div className="p-5 border-b border-gray-100 flex items-center gap-2">
            <Boxes className="w-5 h-5 text-cyan-600" />
            <h2 className="heading-font font-bold text-lg">{lang === "ta" ? "பொருள் வாரியா டிப்போ" : "By Product (Depot)"}</h2>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-gray-50/60">
              <tr className="text-left text-[#86868B] uppercase tracking-wider text-xs">
                <th className="px-4 py-3">{t("name")}</th>
                <th className="px-4 py-3">{t("boxes")}</th>
                <th className="px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {d.by_product.map(p => {
                const low = p.qty < (p.low_stock_threshold || 20);
                return (
                  <tr key={p.product_id} className="hover:bg-gray-50/40" data-testid={`dp-prod-${p.product_id}`}>
                    <td className="px-4 py-3 font-semibold heading-font">
                      {lang === "ta" ? (p.name_ta || p.name) : p.name}
                    </td>
                    <td className="px-4 py-3 font-bold text-[#1D1D1F]">{p.qty}</td>
                    <td className="px-4 py-3">
                      {low ? (
                        <span className="pill bg-red-50 text-red-700"><AlertTriangle className="w-3.5 h-3.5" /> {lang === "ta" ? "குறைவு" : "Low"}</span>
                      ) : (
                        <span className="pill bg-green-50 text-green-700">OK</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="bg-white rounded-2xl soft-shadow overflow-hidden" data-testid="dp-onroad">
          <div className="p-5 border-b border-gray-100 flex items-center gap-2">
            <Truck className="w-5 h-5 text-emerald-600" />
            <h2 className="heading-font font-bold text-lg">{lang === "ta" ? "ஒவ்வொருவரிடம்" : "On-Road by Person"}</h2>
          </div>
          {d.on_road.length === 0 ? (
            <div className="p-8 text-center text-[#86868B]">{lang === "ta" ? "இன்று ஒன்றும் வெளியேறவில்லை" : "Nothing on-road today"}</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50/60">
                <tr className="text-left text-[#86868B] uppercase tracking-wider text-xs">
                  <th className="px-4 py-3">{t("name")}</th>
                  <th className="px-4 py-3">Role</th>
                  <th className="px-4 py-3">Loaded</th>
                  <th className="px-4 py-3">Sold</th>
                  <th className="px-4 py-3">Remaining</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {d.on_road.map(p => (
                  <tr key={p.user_id} className="hover:bg-gray-50/40" data-testid={`dp-or-${p.user_id}`}>
                    <td className="px-4 py-3 font-semibold heading-font">{p.name}</td>
                    <td className="px-4 py-3">
                      <span className={`pill ${p.role === "manager" ? "bg-blue-50 text-blue-700" : "bg-purple-50 text-purple-700"}`}>
                        {p.role === "manager" ? "Mgr" : "Salesman"}
                      </span>
                    </td>
                    <td className="px-4 py-3"><span className="pill bg-blue-50 text-blue-700">{p.loaded}</span></td>
                    <td className="px-4 py-3"><span className="pill bg-green-50 text-green-700">{p.sold}</span></td>
                    <td className="px-4 py-3 font-bold">{p.remaining}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
